import type { OcrRecognitionModel } from '@/core/display-settings'
import type { OcrModelResource } from '@/presets/types'

export interface OcrCdnResource {
  readonly id: string
  readonly name: string
  readonly version: string
  readonly url: string
  readonly fallbackUrls: readonly string[]
  readonly sha256: string
  readonly maxBytes: number
  readonly contentType: string
  readonly legacyCacheUrls?: readonly string[]
  readonly legacyVersions?: readonly string[]
}

const officialModelCdnBaseUrl = 'https://paddle-model-ecology.bj.bcebos.com/paddlex/official_inference_model/paddle3.0.0'
const fallbackModelCdnBaseUrl = 'https://cdn.lunaris.win/qianjiachun/maple-log'
const npmOrtBaseUrl = 'https://registry.npmmirror.com/onnxruntime-web/1.29.0/files/dist'
const jsdelivrOrtBaseUrl = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.29.0/dist'
const detectionFile = 'PP-OCRv6_tiny_det_onnx_infer.tar'
const recognitionFile = 'PP-OCRv6_small_rec_onnx_infer.tar'
const ortMjsFile = 'ort-wasm-simd-threaded.jsep.mjs'
const ortWasmFile = 'ort-wasm-simd-threaded.jsep.wasm'

/** SHA-256 of the official PP-OCRv6 tiny detection tar; Lunaris hosts the same bytes. */
export const OCR_DETECTION_MODEL: OcrCdnResource = {
  id: 'detection',
  name: 'PP-OCRv6_tiny_det',
  version: 'ff6ab415b0a6e0c488550f2fb5d5046f1719848df220b2dc21b56402a65bc05d',
  url: `${officialModelCdnBaseUrl}/${detectionFile}`,
  fallbackUrls: [`${fallbackModelCdnBaseUrl}/${detectionFile}`],
  sha256: 'ff6ab415b0a6e0c488550f2fb5d5046f1719848df220b2dc21b56402a65bc05d',
  maxBytes: 2 * 1024 * 1024,
  contentType: 'application/x-tar',
  legacyCacheUrls: [`${officialModelCdnBaseUrl}/${detectionFile}`],
  legacyVersions: ['ff6ab415b0a6e0c488550f2fb5d5046f1719848df220b2dc21b56402a65bc05d'],
}

/** SHA-256 of the official PP-OCRv6 small recognition tar; Lunaris hosts the same bytes. */
export const OCR_RECOGNITION_MODEL: OcrCdnResource = {
  id: 'recognition',
  name: 'PP-OCRv6_small_rec',
  version: 'd267ab077a44a0eedb1ea8f8c542d263f211de8e9d7a029bf9fcfff7e5a88fb1',
  url: `${officialModelCdnBaseUrl}/${recognitionFile}`,
  fallbackUrls: [`${fallbackModelCdnBaseUrl}/${recognitionFile}`],
  sha256: 'd267ab077a44a0eedb1ea8f8c542d263f211de8e9d7a029bf9fcfff7e5a88fb1',
  maxBytes: 24 * 1024 * 1024,
  contentType: 'application/x-tar',
  legacyCacheUrls: [`${officialModelCdnBaseUrl}/${recognitionFile}`],
  legacyVersions: ['etag--b9b2fa1b0c9760be457b35a8ce149454'],
}

/** SHA-256 of onnxruntime-web@1.29.0 dist/ort-wasm-simd-threaded.jsep.mjs from the npm package. */
export const OCR_ORT_MJS: OcrCdnResource = {
  id: 'ort-mjs',
  name: 'onnxruntime-web-jsep-mjs',
  version: '1.29.0',
  url: `${npmOrtBaseUrl}/${ortMjsFile}`,
  fallbackUrls: [`${jsdelivrOrtBaseUrl}/${ortMjsFile}`],
  sha256: '3d68fa7af88c48894d4b0c8629de12018ab73b77519bc0b05dc8d908ad82749f',
  maxBytes: 128 * 1024,
  contentType: 'text/javascript',
  legacyCacheUrls: [`${npmOrtBaseUrl}/${ortMjsFile}`],
}

/** SHA-256 of onnxruntime-web@1.29.0 dist/ort-wasm-simd-threaded.jsep.wasm from the npm package. */
export const OCR_ORT_WASM: OcrCdnResource = {
  id: 'ort-wasm',
  name: 'onnxruntime-web-jsep-wasm',
  version: '1.29.0',
  url: `${npmOrtBaseUrl}/${ortWasmFile}`,
  fallbackUrls: [`${jsdelivrOrtBaseUrl}/${ortWasmFile}`],
  sha256: 'db816fadbab47a755170c08f933961e231412ac17f5981f9a62e519708a44dea',
  maxBytes: 32 * 1024 * 1024,
  contentType: 'application/wasm',
  legacyCacheUrls: [`${npmOrtBaseUrl}/${ortWasmFile}`],
}

export const OCR_TINY_RECOGNITION_MODEL: OcrCdnResource = {
  id: 'recognition', name: 'PP-OCRv6_tiny_rec',
  url: `${officialModelCdnBaseUrl}/PP-OCRv6_tiny_rec_onnx_infer.tar`,
  fallbackUrls: [`${fallbackModelCdnBaseUrl}/PP-OCRv6_tiny_rec_onnx_infer.tar`],
  version: '1e13b22717b1edd89d4cde4fda272b6c17d5b505c97c2baea99da1a3a2d54b29',
  sha256: '1e13b22717b1edd89d4cde4fda272b6c17d5b505c97c2baea99da1a3a2d54b29',
  maxBytes: 6 * 1024 * 1024, contentType: 'application/x-tar',
}

export const OCR_MODEL_RESOURCES = [OCR_DETECTION_MODEL, OCR_RECOGNITION_MODEL, OCR_TINY_RECOGNITION_MODEL] as const
export const OCR_RUNTIME_RESOURCES = [OCR_ORT_MJS, OCR_ORT_WASM] as const
export const OCR_CDN_RESOURCES = [...OCR_MODEL_RESOURCES, ...OCR_RUNTIME_RESOURCES] as const

export function toOcrModelResource(resource: OcrCdnResource): OcrModelResource {
  return {
    name: resource.name,
    url: resource.url,
    fallbackUrls: resource.fallbackUrls,
    version: resource.version,
    sha256: resource.sha256,
    maxBytes: resource.maxBytes,
  }
}

export function mxdcOcrModels(recognition: OcrRecognitionModel = 'small'): {
  readonly detection: OcrModelResource
  readonly recognition: OcrModelResource
} {
  return {
    detection: toOcrModelResource(OCR_DETECTION_MODEL),
    recognition: toOcrModelResource(recognition === 'tiny' ? OCR_TINY_RECOGNITION_MODEL : OCR_RECOGNITION_MODEL),
  }
}
